import type { ApiError } from "@entrilia/shared/api/types.ts";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import type { GridSortModel } from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import biApi from "../../../../../../api/biApi.ts";
import type { NumberFormatterOptions } from "../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ApArAgingSummaryReportType,
  ApArAgingSummaryResponse,
  BuildApArAgingSummaryReportRequest,
  DimensionDescriptor,
} from "../../../../../../api/types/svTypes.ts";
import useDeferredRequest from "../../../hooks/useDeferredRequest.ts";
import { useAppDispatch } from "../../../sv/store/hooks/useAppDispatch.ts";
import { selectCacheSessionId, selectSessionId } from "../../../sv/store/selectors/currentReportSelectors.ts";
import { selectLogQueries } from "../../../sv/store/selectors/devToolsSelectors.ts";
import {
  selectCurrentDataSetId,
  selectDimensions,
  selectMetaDataLoaded,
  selectMetaDataLoadingFailed,
} from "../../../sv/store/selectors/metaDataSelectors.ts";
import { loadMetaData } from "../../../sv/store/thunks/metaDataThunks.ts";
import { createCurrentReportAction, createDevToolsAction } from "../../../sv/store/utils/storeUtils.ts";
import { DataSetIds, DimensionNames } from "../../../sv/utilities/DimensionNames.ts";
import { formatToISODate } from "../../../utils/dateUtils.ts";
import type { CounterpartyTypeOption } from "../../ap-ar-aging-detail/components/CounterpartyTypeFilter.tsx";
import type { BalanceSheetEntityOption } from "../../balance-sheet/utils/balanceSheetEntityFilter.ts";
import {
  getBalanceSheetEntityDictionaryFromApiResponse,
  getBalanceSheetEntityDimension,
  getBalanceSheetEntityOptions,
} from "../../balance-sheet/utils/balanceSheetEntityFilter.ts";
import type { BalanceSheetPeriodState } from "../../balance-sheet/utils/balanceSheetPeriod.ts";
import {
  getBalanceSheetPeriodRange,
  getBalanceSheetPeriodStateFromPreset,
} from "../../balance-sheet/utils/balanceSheetPeriod.ts";
import type { AgingSummaryReport } from "../types/agingSummaryTypes.ts";
import { useAgingSummaryExport } from "./useAgingSummaryExport.ts";

const errorMessageAutoHideDuration = 5000;

export interface AgingSummaryState {
  // Report data
  report: AgingSummaryReport | undefined;
  reportError: ApiError | undefined;
  isReportFetching: boolean;
  refresh: () => void;

  // Loading
  isLoading: boolean;
  isLoadingFailed: boolean;

  // Build progress
  buildProgress: number | undefined;
  deferredEtag: string | undefined;

  // Period
  period: BalanceSheetPeriodState;
  onPeriodChange: (period: BalanceSheetPeriodState) => void;

  // As-of date
  asOfDate: Date;
  onAsOfDateChange: (date: Date) => void;

  // Entity
  entityOptions: BalanceSheetEntityOption[];
  selectedEntity: BalanceSheetEntityOption | null;
  onEntityChange: (entity: BalanceSheetEntityOption | null) => void;

  // Counterparty type
  counterpartyTypeOptions: CounterpartyTypeOption[];
  selectedCounterpartyType: CounterpartyTypeOption | null;
  onCounterpartyTypeChange: (value: CounterpartyTypeOption | null) => void;

  // Number format
  numberFormat: NumberFormatterOptions | undefined;
  onNumberFormatChange: (format: NumberFormatterOptions | undefined) => void;

  // Sorting
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;

  // Settings
  isSettingsDefault: boolean;
  onResetSettings: () => void;
  onResetFilters: () => void;

  // Export
  exporting: boolean;
  exportValuesAsDisplayed: boolean;
  onExportValuesAsDisplayedChange: (value: boolean) => void;
  onExport: () => void;
  onExportWithParameters: () => void;
}

interface UseAgingSummaryStateParams {
  reportType: ApArAgingSummaryReportType;
}

const createDefaultAgingPeriodState = (now: Date = new Date()): BalanceSheetPeriodState =>
  getBalanceSheetPeriodStateFromPreset("Today", now);

const getAsOfDateFromPeriod = (periodState: BalanceSheetPeriodState): Date =>
  getBalanceSheetPeriodRange(periodState).to;

const extractCounterpartyTypeRows = (data: unknown): Record<string, unknown>[] => {
  if (!data || typeof data !== "object") return [];
  const key = DimensionNames.ApArCounterpartyTypeCode;

  if (!Array.isArray(data) && key in data) {
    const rows = (data as Record<string, unknown>)[key];
    return Array.isArray(rows)
      ? rows.filter((r): r is Record<string, unknown> => typeof r === "object" && r !== null)
      : [];
  }

  if (Array.isArray(data)) {
    return data
      .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
      .map((item) => item[key])
      .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null);
  }

  return [];
};

const getCounterpartyTypeOptions = (data: unknown): CounterpartyTypeOption[] => {
  const rows = extractCounterpartyTypeRows(data);
  const byCode = new Map<string, CounterpartyTypeOption>();
  for (const row of rows) {
    const code = String(row["Code"] ?? "").trim();
    if (!code) continue;
    if (!byCode.has(code)) {
      const description = String(row["Description"] ?? "").trim();
      byCode.set(code, { code, description });
    }
  }
  return [...byCode.values()].sort((a, b) => a.code.localeCompare(b.code));
};

const useAgingSummaryState = ({ reportType }: UseAgingSummaryStateParams): AgingSummaryState => {
  const dispatch = useAppDispatch();
  const { sendNotificationError } = useNotificationContext();
  const dataSetId = useSelector(selectCurrentDataSetId) ?? DataSetIds.GeneralAndMemo;
  const dimensions = useSelector(selectDimensions);
  const isMetaDataLoaded = useSelector(selectMetaDataLoaded);
  const isMetaDataLoadingFailed = useSelector(selectMetaDataLoadingFailed);

  useEffect(() => {
    dispatch(loadMetaData());
  }, [dispatch]);

  const loadEntityDictionary = useCallback(
    () => biApi.loadDimensionDictionaries(dataSetId, DimensionNames.EntityNo),
    [dataSetId]
  );
  const [entityDictionaryResponse, entityDictionaryError] = useFetch<unknown>(loadEntityDictionary);

  const loadCounterpartyTypeDictionary = useCallback(
    () => biApi.loadDimensionDictionaries(dataSetId, DimensionNames.ApArCounterpartyTypeCode),
    [dataSetId]
  );
  const [counterpartyTypeDictionaryResponse, counterpartyTypeDictionaryError] =
    useFetch<unknown>(loadCounterpartyTypeDictionary);

  const entityDimension = useMemo<DimensionDescriptor | undefined>(
    () => getBalanceSheetEntityDimension(dimensions),
    [dimensions]
  );
  const entityDictionary = useMemo(
    () => getBalanceSheetEntityDictionaryFromApiResponse(entityDictionaryResponse),
    [entityDictionaryResponse]
  );
  const entityOptions = useMemo(
    () => getBalanceSheetEntityOptions(entityDimension, entityDictionary),
    [entityDimension, entityDictionary]
  );

  const counterpartyTypeOptions = useMemo(
    () => getCounterpartyTypeOptions(counterpartyTypeDictionaryResponse),
    [counterpartyTypeDictionaryResponse]
  );

  const sessionId = useSelector(selectSessionId);
  const cacheSessionId = useSelector(selectCacheSessionId);
  const isQueryLoggingActive = useSelector(selectLogQueries) === true;

  const isLoadingFailed =
    isMetaDataLoadingFailed === true || !!entityDictionaryError || !!counterpartyTypeDictionaryError;
  const isLoading =
    !isLoadingFailed &&
    (!isMetaDataLoaded || entityDictionaryResponse === undefined || counterpartyTypeDictionaryResponse === undefined);

  // User-controlled state
  const [period, setPeriod] = useState<BalanceSheetPeriodState>(createDefaultAgingPeriodState);
  const [asOfDate, setAsOfDate] = useState<Date>(() => getAsOfDateFromPeriod(createDefaultAgingPeriodState()));
  const [selectedEntity, setSelectedEntity] = useState<BalanceSheetEntityOption | null>(null);
  const [selectedCounterpartyType, setSelectedCounterpartyType] = useState<CounterpartyTypeOption | null>(null);
  const [exportValuesAsDisplayed, setExportValuesAsDisplayed] = useState(false);
  const [numberFormat, setNumberFormat] = useState<NumberFormatterOptions | undefined>(undefined);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const handlePeriodChange = useCallback((newPeriod: BalanceSheetPeriodState) => {
    setPeriod(newPeriod);
    setAsOfDate(getAsOfDateFromPeriod(newPeriod));
  }, []);

  // Build request (memoized — changes on any input change)
  const buildRequest = useMemo<BuildApArAgingSummaryReportRequest>(
    () => ({
      postingDate: formatToISODate(asOfDate),
      entityNo: selectedEntity?.entityNo ?? null,
      counterpartyTypeCode: selectedCounterpartyType?.code ?? null,
      reportType,
      sessionId,
      cacheSessionId: cacheSessionId ?? "",
      useQueryLogging: isQueryLoggingActive,
      numberFormat: numberFormat ?? null,
    }),
    [
      asOfDate,
      selectedEntity,
      selectedCounterpartyType,
      reportType,
      sessionId,
      cacheSessionId,
      isQueryLoggingActive,
      numberFormat,
    ]
  );

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const {
    deferredValue: deferredRequest,
    buildProgress,
    deferredEtag,
  } = useDeferredRequest(buildRequest, hasLoadedOnce);

  const loadReport = useCallback(
    (signal?: AbortSignal) => biApi.buildApArAgingSummaryReport(deferredRequest, signal),
    [deferredRequest]
  );

  const [reportData, reportError, { isFetching: isReportFetching, fetch: refresh }] =
    useFetch<ApArAgingSummaryResponse>(loadReport);

  useEffect(() => {
    if (!reportError) {
      return;
    }

    const errorMessage =
      typeof reportError.message === "string" && reportError.message.length > 0
        ? reportError.message
        : "An error occurred while calculating report data. Please try again.";
    sendNotificationError(errorMessage, errorMessageAutoHideDuration);
  }, [reportError, sendNotificationError]);

  useEffect(() => {
    if (reportData && !hasLoadedOnce) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setHasLoadedOnce(true);
    }
  }, [reportData, hasLoadedOnce]);

  useEffect(() => {
    dispatch(createCurrentReportAction("setEtag", { etag: reportData?.etag, cacheValid: undefined }));
  }, [dispatch, reportData?.etag]);

  useEffect(() => {
    dispatch(createDevToolsAction("update", { loggingItems: reportData?.loggingItems }));
  }, [dispatch, reportData?.loggingItems]);

  useEffect(() => {
    if (!isQueryLoggingActive) {
      dispatch(createDevToolsAction("update", { loggingItems: undefined }));
    }
  }, [dispatch, isQueryLoggingActive]);

  const report = useMemo<AgingSummaryReport | undefined>(() => {
    if (!reportData) return undefined;
    return {
      columns: reportData.columns,
      rows: reportData.data.map((r) => ({ id: r.id, data: r.data })),
      totals: reportData.grandTotals && Object.keys(reportData.grandTotals).length > 0 ? reportData.grandTotals : null,
    };
  }, [reportData]);

  const { exporting, requestExportWithoutParameters, requestExportWithParameters } = useAgingSummaryExport(
    deferredRequest,
    sortModel,
    report?.columns ?? [],
    exportValuesAsDisplayed
  );

  const isSettingsDefault = numberFormat === undefined;

  const handleResetSettings = useCallback(() => {
    setNumberFormat(undefined);
  }, []);

  const handleResetFilters = useCallback(() => {
    const defaultPeriod = createDefaultAgingPeriodState();
    setPeriod(defaultPeriod);
    setAsOfDate(getAsOfDateFromPeriod(defaultPeriod));
    setSelectedEntity(null);
    setSelectedCounterpartyType(null);
  }, []);

  return {
    report,
    reportError,
    isReportFetching,
    refresh,
    isLoading,
    isLoadingFailed,
    buildProgress,
    deferredEtag,
    period,
    onPeriodChange: handlePeriodChange,
    asOfDate,
    onAsOfDateChange: setAsOfDate,
    entityOptions,
    selectedEntity,
    onEntityChange: setSelectedEntity,
    counterpartyTypeOptions,
    selectedCounterpartyType,
    onCounterpartyTypeChange: setSelectedCounterpartyType,
    numberFormat,
    onNumberFormatChange: setNumberFormat,
    sortModel,
    onSortModelChange: setSortModel,
    isSettingsDefault,
    onResetSettings: handleResetSettings,
    onResetFilters: handleResetFilters,
    exporting,
    exportValuesAsDisplayed,
    onExportValuesAsDisplayedChange: setExportValuesAsDisplayed,
    onExport: requestExportWithoutParameters,
    onExportWithParameters: requestExportWithParameters,
  };
};

export default useAgingSummaryState;
